/**
 * Catalogue of pages a system update can link to with a `[[key]]` marker in its body.
 *
 * Mirrors the routes of `menu.config.ts` / `app.routes.ts` and lives next to the update feature so a
 * new menu entry is one line here. Labels are translation keys — the menu's own labels where the
 * menu has one, `ADMIN.systemUpdates.pages.*` otherwise. `route` is router-relative (no `/app`
 * base href); `access` is the `acccess.config.ts` key of the route (null = any signed-in user).
 */
export interface SystemPage {
  /** The text between `[[` and `]]` — lowercase, stable once published. */
  key: string;
  /** Translation key of the chip label. */
  label: string;
  route: string;
  access: string | null;
}

export const SYSTEM_PAGES: readonly SystemPage[] = [
  { key: 'dashboard', label: 'ADMIN.systemUpdates.pages.dashboard', route: '/dashboard', access: 'dashboard' },

  // Messages
  { key: 'new-campaign', label: 'New campaign', route: '/campaigns/new', access: 'campaigns' },
  { key: 'campaigns', label: 'Campaigns', route: '/campaigns', access: 'campaigns' },
  { key: 'reports', label: 'Reports', route: '/reports', access: 'reports' },
  { key: 'label-statistics', label: 'Label statistics', route: '/label-statistics', access: 'label-statistics' },
  { key: 'hierarchy-report', label: 'Hierarchy Report', route: '/hierarchy-report', access: 'hierarchy-report' },
  { key: 'media-library', label: 'Message Media library', route: '/medialibrary', access: 'medialibrary' },

  // Presentation
  { key: 'presentations', label: 'Presentations (Beta)', route: '/designer', access: 'designer' },
  {
    key: 'presentation-reports',
    label: 'ADMIN.systemUpdates.pages.presentationReports',
    route: '/presentation-reports',
    access: 'presentations',
  },
  { key: 'meetings', label: 'Remote Meetings (legacy)', route: '/meetings', access: 'meetings' },

  // Forms
  { key: 'forms', label: 'Forms', route: '/agreement-forms', access: 'forms' },

  // Contacts
  { key: 'contacts', label: 'Contacts', route: '/contacts', access: 'contacts' },
  { key: 'contact-import', label: 'Importer', route: '/contacts/import', access: 'contacts' },
  { key: 'workplaces', label: 'Workplaces', route: '/workplaces', access: 'workplaces' },
  { key: 'groups', label: 'Groups', route: '/groups', access: 'groups' },
  { key: 'specializations', label: 'Specializations', route: '/specializations', access: 'specializations' },
  { key: 'custom-fields', label: 'Custom fields', route: '/custom-fields', access: 'custom-fields' },
  { key: 'leads', label: 'Leads', route: '/leads', access: 'leads' },
  { key: 'segments', label: 'Segmentation', route: '/segments', access: 'segments' },

  // Automator
  { key: 'automator', label: 'Automator', route: '/workflow', access: 'workflow' },

  // Approval
  { key: 'approve-review', label: 'Review Inbox', route: '/approve/review', access: 'approve' },
  { key: 'approve-reference-documents', label: 'Reference Documents', route: '/approve/reference-documents', access: 'approve' },
  { key: 'approve-policy-rules', label: 'Policy Rules', route: '/approve/policy-rules', access: 'approve' },

  // Administration
  { key: 'labels', label: 'Labels', route: '/labels', access: null },
  { key: 'team-members', label: 'Team members', route: '/team-management', access: 'team-management' },
  { key: 'org-structure', label: 'Org Structure', route: '/org-structure', access: 'org-structure' },
  { key: 'hierarchy-team', label: 'Hierarchy Team', route: '/hierarchy-team-management', access: 'hierarchy-team-management' },
  { key: 'billing', label: 'Billing', route: '/billing', access: 'billing' },
  { key: 'knowhow', label: 'Know-How AI', route: '/knowhow', access: 'knowhow' },
  { key: 'ai-usage', label: 'AI Usage', route: '/ai-usage', access: 'ai-usage' },
  { key: 'batch-jobs', label: 'Batch jobs', route: '/batch-job', access: 'batch-job' },
  { key: 'migrations', label: 'Migrations', route: '/migrations', access: 'migrations' },
  { key: 'specialization-vocabulary', label: 'Standard specializations', route: '/specialization-vocabulary', access: 'specialization-vocabulary' },
  { key: 'consent', label: 'Consent policies', route: '/consent', access: 'consent' },
  { key: 'configuration', label: 'Configuration Manager', route: '/configuration', access: 'configuration' },

  // This feature
  { key: 'system-updates', label: "What's new", route: '/system-updates', access: 'system-updates' },
  { key: 'system-updates-admin', label: 'System Updates', route: '/system-updates/admin', access: 'system-updates.manage' },
];

export function findSystemPage(key: string, pages: readonly SystemPage[] = SYSTEM_PAGES): SystemPage | undefined {
  const normalized = key.trim().toLowerCase();
  return pages.find((page) => page.key === normalized);
}
